import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { VERIFY_USER_TOKEN_QUERY, UPDATE_USER_PASSWORD_QUERY } from '../services/GraphQLQueries'
import { setAuthToken } from '../services/auth'

export default function ActualResetPassword() {
  const navigate = useNavigate()
  const location = useLocation()
  const [tokenValid, setTokenValid] = useState(false)
  const [isVerifying, setIsVerifying] = useState(true)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const [verifyUserToken] = useMutation(VERIFY_USER_TOKEN_QUERY)
  const [updateUserPassword] = useMutation(UPDATE_USER_PASSWORD_QUERY)

  // ✅ Extract token from URL
  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')

    useEffect(() => {
      if (!token) {
        setErrorMessage('Invalid reset link.')
        setIsVerifying(false)
        return
      }

    // ✅ Verify token
    verifyUserToken({ variables: { resetToken: token } })
      .then(({ data }) => {
        if (data?.verifyUserToken?.bearerToken) {
          setAuthToken(data.verifyUserToken.bearerToken)
          setTokenValid(true)
        } else {
          setErrorMessage('Reset link is invalid or expired.')
        }
      })
      .catch((err) => {
        console.error('Verification failed:', err)
        setErrorMessage('Could not verify reset link.')
      })
      .finally(() => setIsVerifying(false))
  }, [token, verifyUserToken])

  // ✅ Submit new password
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.')
      return
    }

    try {
      const { data } = await updateUserPassword({ variables: { password } })
      if (data.updateUserPassword.success) {
        setSuccessMessage('Your password has been updated.')
        setTimeout(() => navigate('/signin'), 2000)
      } else {
        setErrorMessage(data.updateUserPassword.error || 'Something went wrong.')
      }
    } catch (error) {
      console.error('Update password failed:', error)
      setErrorMessage('Unable to update password.')
    }
  }

  // 🌀 Loading state
  if (isVerifying) {
    return (
      <div className="flex items-center justify-center h-screen text-lg text-gray-600">
        Verifying reset link...
      </div>
    )
  }

  // ❌ Invalid token
  if (!tokenValid) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-50 border border-red-200 text-red-600 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-2">Invalid Reset Link</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    )
  }

  // ✅ Password reset form
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-md border border-gray-200">
        <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Reset Your Password</h1>

        {errorMessage && (
          <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm text-center">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md text-sm text-center">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="on">
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter new password"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Confirm new password"
              autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-semibold transition"
          >
            Update Password
          </button>
        </form>
      </div>
    </div>
  )
}
